import { CampaignTypeEnum, ChannelEnum } from "./enums";
import { isBirthdayIncompleteProfileField } from "./labels";

const SMS_DEFAULTS: Partial<Record<CampaignTypeEnum, string>> = {
  [CampaignTypeEnum.Voucher]:
    "Your {VoucherName} is available. Use it before it expires.",
  [CampaignTypeEnum.Point]:
    "Your {MemberPointBalanceExpiring} points are expiring soon. Use them before they expire.",
  [CampaignTypeEnum.VoucherPoint]:
    "Enjoy your vouchers and points. Use them before they expire.",
  [CampaignTypeEnum.EVoucher]:
    "Get more value with our e-voucher bundle. Limited time offer.",
  [CampaignTypeEnum.Custom]:
    "Enjoy your available rewards on your next visit. Use them before they expire.",
};

const WHATSAPP_DEFAULTS: Partial<Record<CampaignTypeEnum, string>> = {
  [CampaignTypeEnum.Point]:
    "<p>Something’s waiting for you 👋</p><br /><p>Hi <strong>{FirstName}</strong> <strong>{LastName}</strong>,</p><br /><p>Don’t let your points go to waste! They’ll expire soon</p><br /><p>You have <strong>{MemberPointBalanceExpiring} Points</strong> expiring on <strong>{MemberPointBalanceNextExpiryDate}</strong>.</p><br /><p>✅ Redeem anytime before expiry</p><p>✅ Enjoy your benefit while it lasts</p>",
  [CampaignTypeEnum.Custom]:
    "<p>A little treat for you 🎁</p><br /><p>Hi <strong>{FirstName}</strong> <strong>{LastName}</strong>,</p<br /><p>Don’t miss out on your rewards before they expire.</p><br /><p>You have rewards available in your account — use them to enjoy even more savings!</p><ul><li>You have <strong>{MemberBalanceVoucher} Voucher(s)</strong> available to use.</li><li>You have <strong>{MemberPointBalance} Points</strong> available to use.</li></ul><br /><p>✅ Redeem anytime before expiry</p><p>✅ Enjoy your benefit while it lasts</p>",
};

function getWhatsAppVoucherListItem(redemptionDateBasedOn?: number): string {
  if (redemptionDateBasedOn === 2) {
    return "<li><strong>{VoucherName}</strong>, Valid for <strong>{VoucherValidDays}</strong> Days</li>";
  }
  if (redemptionDateBasedOn === 3) {
    return "<li><strong>{VoucherName}</strong> is available for redemption.</li>";
  }
  return "<li><strong>{VoucherName}</strong>, will expire on <strong>{VoucherExpiry}</strong></li>";
}

function getWhatsAppVoucherPointListItem(
  redemptionDateBasedOn?: number,
): string {
  if (redemptionDateBasedOn === 2) {
    return "<li>Your <strong>{VoucherName}</strong>, Valid for <strong>{VoucherValidDays}</strong> Days</li>";
  }
  if (redemptionDateBasedOn === 3) {
    return "<li>Your <strong>{VoucherName}</strong> is available for redemption.</li>";
  }
  return "<li>Your <strong>{VoucherName}</strong> will expire on <strong>{VoucherExpiry}</strong>.</li>";
}

function getWhatsAppEVoucherListItems(
  eVouchers?: { Name: string; Price: number }[],
): string {
  if (!eVouchers || eVouchers.length === 0) {
    return "<li><strong>{EVoucherName}</strong> - available at <strong>{EVoucherPrice}</strong>.</li>";
  }
  return eVouchers
    .map(
      (v) =>
        `<li><strong>${v.Name}</strong> - available at <strong>${v.Price}</strong>.</li>`,
    )
    .join("");
}

type ProfileCompletionReminderEvent =
  | "birthdayOnly"
  | "general"
  | "birthdayAndMore";

function getProfileCompletionReminderEvent(
  incompleteProfileFields?: string[],
): ProfileCompletionReminderEvent {
  const fields = incompleteProfileFields ?? [];
  const hasBirthday = fields.some(isBirthdayIncompleteProfileField);
  if (!hasBirthday) return "general";
  return fields.length === 1 ? "birthdayOnly" : "birthdayAndMore";
}

const PROFILE_COMPLETION_REMINDER_SMS: Record<
  ProfileCompletionReminderEvent,
  string
> = {
  birthdayOnly:
    "Hi {FirstName}, complete your birthday information to unlock your birthday rewards.",
  general:
    "Hi {FirstName}, your profile is incomplete. Please update your missing information.",
  birthdayAndMore:
    "Hi {FirstName}, complete your remaining profile information, including your birthday, to unlock birthday rewards.",
};

const PROFILE_COMPLETION_REMINDER_WHATSAPP: Record<
  ProfileCompletionReminderEvent,
  string
> = {
  birthdayOnly:
    "<p><strong>Complete Your Birthday Information</strong> 🎉</p><br /><p>Hi <strong>{FirstName}</strong> 👋</p><p>You're just one step away from enjoying your birthday rewards!</p><br /><p>Please complete your Birthday information in your profile.</p>",
  general:
    "<p><strong>Complete Your Profile</strong> 👤</p><br /><p>Hi <strong>{FirstName}</strong> 👋</p><p>Your member profile is not yet complete.</p><br /><p>Missing information:</p><p>{IncompleteProfileFields}</p><br /><p>Please complete your profile to keep your member information up to date.</p>",
  birthdayAndMore:
    "<p><strong>Complete Your Profile</strong> 👤</p><br /><p>Hi <strong>{FirstName}</strong> 👋</p><p>Your profile is almost complete.</p><br /><p>Please update the following information:</p><p>{IncompleteProfileFields}</p><br /><p>Completing your birthday helps unlock your birthday rewards, while completing your profile keeps your member information up to date.</p>",
};

const PROFILE_COMPLETION_REMINDER_CTA_URL = "https://getz.co/a/CTALINK";

const DEFAULT_WHATSAPP_OPT_IN_FOOTER =
  "\n👉 Reply with *OK* to receive your membership login link\n👉 Reply with *STOP* to opt out of future WhatsApp messages";

export const PROFILE_COMPLETION_REMINDER_WHATSAPP_FOOTER = `\nUpdate your profile here:\n👉 ${PROFILE_COMPLETION_REMINDER_CTA_URL}`;

export const PROFILE_COMPLETION_REMINDER_SMS_FOOTER = `\n\nUpdate here:\n${PROFILE_COMPLETION_REMINDER_CTA_URL}`;

export function getWhatsappFooterText(
  campaignType: CampaignTypeEnum | number | undefined,
): string {
  if (campaignType === CampaignTypeEnum.ProfileCompletionReminder) {
    return PROFILE_COMPLETION_REMINDER_WHATSAPP_FOOTER;
  }
  return DEFAULT_WHATSAPP_OPT_IN_FOOTER;
}

export function getDefaultMessage(
  campaignType: CampaignTypeEnum | number | undefined,
  channel: ChannelEnum | null,
  redemptionDateBasedOn?: number,
  eVouchers?: { Name: string; Price: number }[],
  incompleteProfileFields?: string[],
): string {
  if (!campaignType || !channel) return "";

  if (campaignType === CampaignTypeEnum.ProfileCompletionReminder) {
    const event = getProfileCompletionReminderEvent(incompleteProfileFields);
    if (channel === ChannelEnum.WhatsAppStandard) {
      return PROFILE_COMPLETION_REMINDER_WHATSAPP[event];
    }
    if (channel === ChannelEnum.Sms) {
      return PROFILE_COMPLETION_REMINDER_SMS[event];
    }
    return "";
  }

  if (channel === ChannelEnum.WhatsAppStandard) {
    if (campaignType === CampaignTypeEnum.Voucher) {
      const liItem = getWhatsAppVoucherListItem(redemptionDateBasedOn);
      return `<p>Something’s waiting for you 🎁</p><br /><p>Hi <strong>{FirstName}</strong> <strong>{LastName}</strong>,</p><br /><p>Don’t miss out on your Voucher!</p><br /><p>You have vouchers available in your account</p><ul>${liItem}</ul><br /><p>Reward Applicable Services:</p>{RedemptionOrderTagsDisplayName}`;
    }
    if (campaignType === CampaignTypeEnum.VoucherPoint) {
      const liItem = getWhatsAppVoucherPointListItem(redemptionDateBasedOn);
      return `<p>Something’s waiting for you 👋</p><br /><p>Hi <strong>{FirstName}</strong> <strong>{LastName}</strong>,</p><br /><p>Don’t miss out on your rewards before they expire.</p><br /><p>You have rewards available — combine them to enjoy even more savings!</p><ul>${liItem}<li>Your <strong>{MemberPointBalanceExpiring} Points</strong> expiring on <strong>{MemberPointBalanceNextExpiryDate}</strong>.</li></ul><br /><p>✅ Redeem anytime before expiry</p><p>✅ Enjoy your benefit while it lasts</p>`;
    }
    if (campaignType === CampaignTypeEnum.EVoucher) {
      const liItems = getWhatsAppEVoucherListItems(eVouchers);
      return `<p>A little treat for you 🎁</p><br /><p>Hi <strong>{FirstName}</strong> <strong>{LastName}</strong>,</p><br /><p>Treat yourself or share the joy with friends with our brand new e-vouchers:</p><ul>${liItems}</ul><br /><p>Grab yours now and enjoy big savings. T&Cs apply. Don’t miss out — limited time only!</p>`;
    }
    return WHATSAPP_DEFAULTS[campaignType as CampaignTypeEnum] ?? "";
  }

  if (channel === ChannelEnum.Sms) {
    return SMS_DEFAULTS[campaignType as CampaignTypeEnum] ?? "";
  }

  return "";
}
